import React, { useState, useEffect } from 'react'
import { useSheetDragDismiss } from '../hooks/useSheetDragDismiss'
import FormInput from './FormInput'
import { setAppStatusBarDark } from '../utils/flutterStatusBar'
import { toastUtil } from '../utils/toast'
import { reviewCategoryApi } from '../api/reviewCategory'
import Gap from './Gap'
import SheetHeader from './SheetHeader'
import SubmitButton from './SubmitButton'

interface ReviewCategoryAddModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (category: {
    id: number
    name: string
    created_at: string
  }) => void
}

const ReviewCategoryAddModal: React.FC<ReviewCategoryAddModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [categoryName, setCategoryName] = useState('')

  useEffect(() => {
    setAppStatusBarDark(isOpen)
    return () => setAppStatusBarDark(false)
  }, [isOpen])

  // 모달이 열릴 때마다 로컬 변수 초기화
  useEffect(() => {
    if (isOpen) {
      setCategoryName('')
    }
  }, [isOpen])

  const handleSubmit = async () => {
    if (!categoryName.trim()) {
      toastUtil.error('카테고리 이름을 입력해주세요')
      return
    }

    try {
      const response = await reviewCategoryApi.createCategory({
        name: categoryName.trim(),
      })
      toastUtil.success('카테고리가 추가되었습니다')
      setCategoryName('')
      onClose()
      onSuccess(response.category)
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || '카테고리 추가에 실패했습니다'
      toastUtil.error(errorMessage)
    }
  }

  const handleClose = () => {
    setCategoryName('')
    onClose()
  }

  const { handleProps, sheetStyle } = useSheetDragDismiss({
    onClose: handleClose,
    enabled: isOpen,
  })

  if (!isOpen) return null

  return (
    <>
      {/* 오버레이 */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[1040]"
        onClick={handleClose}
      />

      {/* 바텀 시트 컨테이너 */}
      <div className="fixed bottom-0 left-0 right-0 z-[1050] flex justify-center">
        <div className="app-container flex flex-col" style={sheetStyle}>
          {/* 바텀 시트 */}
          <div
            className={`
              bg-ivoryKey
              rounded-t-30px
              shadow-lg
              overflow-hidden
              flex
              flex-col
              w-full
            `}
            style={{
              height: `${Math.floor((422 / 844) * window.innerHeight)}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 드래그 핸들 (아래로 끌면 닫힘) */}
            <div className="flex flex-col w-full" {...handleProps}>
              <Gap height={24} />
              <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto"></div>
              <Gap height={20} />
            </div>

            {/* 헤더 (공용) */}
            <SheetHeader title="카테고리 추가" />

            <Gap height={36} />

            {/* 이름 입력 */}
            <FormInput
              label=""
              name="categoryName"
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="카테고리 이름을 입력하세요"
              marginBottom={0}
              addClassName="px-20px"
            />

            <Gap height={32} />

            {/* 완료 버튼 */}
            <div className="px-20px">
              <SubmitButton
                text="완료"
                onSubmit={handleSubmit}
                onDisabled={handleSubmit}
                disabled={!categoryName.trim()}
              />
            </div>

            <Gap height={32} />
          </div>
        </div>
      </div>
    </>
  )
}

export default ReviewCategoryAddModal
